import type { AppConfigMasteryLevelsType } from "@memorang/constants/config/schema";
import {
	type ItemTypeDetailStats,
	type QuestionItem,
	type QuestionTypeRowData,
	type SkillFilter,
	collectQuestionItemsForSkillFilter,
	getQuestionItemsForSkillFilter,
	getReportSkills,
	isQuestionItem,
} from "@memorang/helpers/report";
import {
	compareTagSortOrder,
	isProficiencyTag,
	isQuestionTypeTag,
} from "@memorang/helpers/tag";
import type { BaseItemSchema, Item, MixedItemTag } from "@memorang/types";
import type { TFunction } from "i18next";
import { getSkillIcon } from "../../../constants";
import type { IconType } from "../../../icon";
import type { ScoreChipProps } from "../../ScoreChip";

export type { QuestionItem, QuestionTypeRowData, SkillFilter };
export { collectQuestionItemsForSkillFilter, isQuestionItem };

export type SessionReportQuestionTypeCardData = {
	title: string;
	subtitle: string;
	icon?: IconType;
	filter?: string;
	tagItems: Item[];
	sessionItems: BaseItemSchema[];
	proficiencyTags: MixedItemTag[];
	questionTypeTags: MixedItemTag[];
};

const hasNestedSessionItems = (
	item: BaseItemSchema,
): item is BaseItemSchema & { items: BaseItemSchema[] } =>
	"items" in item && Array.isArray(item.items) && item.items.length > 0;

export const flattenSessionItemsForLookup = (
	sessionItems: BaseItemSchema[],
): BaseItemSchema[] => {
	return sessionItems.flatMap((item) => {
		if (hasNestedSessionItems(item)) {
			return [item, ...flattenSessionItemsForLookup(item.items)];
		}
		return [item];
	});
};

export const buildSessionReportQuestionTypeCards = ({
	tags,
	topics,
	sessionItems,
	t,
}: {
	tags: MixedItemTag[];
	topics: string[];
	sessionItems: BaseItemSchema[];
	t: TFunction;
}): SessionReportQuestionTypeCardData[] => {
	const proficiencyTags = tags.filter((tag) => isProficiencyTag(tag));
	const questionTypeTags = tags.filter((tag) => isQuestionTypeTag(tag));

	const skillTags = getReportSkills(tags, topics);
	if (skillTags.length > 1) {
		const skillGroups = skillTags.flatMap((skill) => {
			const questionItems = (skill.items ?? []).filter(isQuestionItem);
			const itemTypeCount = Object.keys(
				groupQuestionItemsByQuestionType(questionItems, sessionItems),
			).length;

			return [
				{
					title: t("report.questionTypeCardTitle", {
						skill: skill.title,
					}),
					subtitle: t("report.questionTypeCardSubtitle", {
						count: itemTypeCount,
						skill: skill.title,
					}),
					icon: getSkillIcon(skill.title),
					filter: skill.title,
					tagItems: skill.items ?? [],
					sessionItems,
					proficiencyTags,
					questionTypeTags,
				},
			];
		});

		if (skillGroups.length > 0) {
			return skillGroups;
		}
	}

	if (questionTypeTags.length === 0) {
		return [];
	}

	return [
		{
			title: t("report.itemTypesTitle"),
			subtitle: t("report.questionTypeCardAltSubtitle", {
				count: questionTypeTags.length,
			}),
			icon: "format-list-bulleted-type",
			tagItems: tags.flatMap((tag) => tag.items ?? []),
			sessionItems,
			proficiencyTags,
			questionTypeTags,
		},
	];
};

export const findQuestionTypeTotalResponses = (
	questionTypeTags: MixedItemTag[] | undefined,
	questionType: string,
): number =>
	questionTypeTags?.find((tag) => tag.title === questionType)?.totalResponses ??
	0;

const buildSessionItemByIdMap = (
	sessionItems: BaseItemSchema[],
): Map<number, BaseItemSchema> => {
	const sessionItemById = new Map<number, BaseItemSchema>();
	for (const item of flattenSessionItemsForLookup(sessionItems)) {
		if (item.id != null && !sessionItemById.has(item.id)) {
			sessionItemById.set(item.id, item);
		}
	}
	return sessionItemById;
};

const resolveQuestionTypeTagForItem = (
	questionItem: QuestionItem,
	sessionItemById: Map<number, BaseItemSchema>,
): MixedItemTag | undefined => {
	const sessionItem = sessionItemById.get(questionItem.id);
	return sessionItem?.tags?.find((tag) => isQuestionTypeTag(tag));
};

export const groupQuestionItemsByQuestionType = (
	questionItems: QuestionItem[],
	sessionItems: BaseItemSchema[],
): Record<string, QuestionItem[]> => {
	const sessionItemById = buildSessionItemByIdMap(sessionItems);

	return questionItems.reduce(
		(acc, questionItem) => {
			const questionType =
				resolveQuestionTypeTagForItem(questionItem, sessionItemById)?.title ??
				questionItem.variant;
			if (!acc[questionType]) {
				acc[questionType] = [];
			}
			const isDuplicate = acc[questionType].some(
				(item) => item.id === questionItem.id,
			);
			if (!isDuplicate) {
				acc[questionType].push(questionItem);
			}
			return acc;
		},
		{} as Record<string, QuestionItem[]>,
	);
};

const buildQuestionTypeTagByTitleMap = (
	sessionItems: BaseItemSchema[],
): Map<string, MixedItemTag> => {
	const tagByTitle = new Map<string, MixedItemTag>();
	for (const item of flattenSessionItemsForLookup(sessionItems)) {
		const tag = item.tags?.find((candidate) => isQuestionTypeTag(candidate));
		if (tag && !tagByTitle.has(tag.title)) {
			tagByTitle.set(tag.title, tag);
		}
	}
	return tagByTitle;
};

export const getDistributionForItems = (
	questionItems: QuestionItem[],
): string => {
	const distributionCounts = questionItems.reduce(
		(acc, questionItem) => {
			const distribution = questionItem.distribution;
			if (!acc[distribution]) {
				acc[distribution] = 0;
			}
			acc[distribution]++;
			return acc;
		},
		{} as Record<string, number>,
	);
	return Object.entries(distributionCounts).sort((a, b) => b[1] - a[1])[0][0];
};

export const sortQuestionTypeRows = <T extends { questionType: string }>(
	rows: T[],
	sessionItems: BaseItemSchema[],
): T[] => {
	const tagByTitle = buildQuestionTypeTagByTitleMap(sessionItems);
	return [...rows].sort((a, b) => {
		const tagA = tagByTitle.get(a.questionType);
		const tagB = tagByTitle.get(b.questionType);
		if (tagA && tagB) {
			return compareTagSortOrder(tagA, tagB);
		}
		if (tagA || tagB) {
			return tagA ? -1 : 1;
		}
		return a.questionType.localeCompare(b.questionType);
	});
};

export const buildQuestionTypeRowsFromItems = (
	questionItems: QuestionItem[],
	sessionItems: BaseItemSchema[],
): QuestionTypeRowData[] => {
	if (questionItems.length === 0) {
		return [];
	}

	const questionItemsByQuestionType = groupQuestionItemsByQuestionType(
		questionItems,
		sessionItems,
	);

	const rows = Object.entries(questionItemsByQuestionType).map(
		([questionType, items]) => ({
			questionType,
			numItems: items.length,
			distribution: getDistributionForItems(items),
		}),
	);

	return sortQuestionTypeRows(rows, sessionItems);
};

export const buildMasteryLevelByDistributionMap = (
	masteryLevels?: AppConfigMasteryLevelsType,
): Map<string, AppConfigMasteryLevelsType[number]> => {
	if (!masteryLevels) {
		return new Map();
	}
	// Distribution buckets from the report API match config `id`, not the
	// coarse `level` grouping (e.g. PTE medium has level "low").
	return new Map(masteryLevels.map((level) => [level.id, level]));
};

export const resolveFilter = (
	param: string | undefined,
	allowedValues: string[],
): SkillFilter => {
	if (!param?.trim() || allowedValues.length === 0) {
		return "all";
	}
	const normalized = param.trim().toLowerCase();
	const match = allowedValues.find(
		(value) => value.toLowerCase() === normalized,
	);
	return match ?? "all";
};

/**
 * Maps aggregate points for a question type to ScoreChip props.
 */
export const buildAggregateScoreChipProps = (
	stats: ItemTypeDetailStats,
): Omit<ScoreChipProps, "minWidth"> | undefined => {
	const { pointsEarned, pointsTotal } = stats;
	if (pointsEarned == null || pointsTotal == null || pointsTotal <= 0) {
		return undefined;
	}
	return {
		value: `${Math.round(pointsEarned)}/${pointsTotal}`,
		scorePercentage: (pointsEarned / pointsTotal) * 100,
		isCorrect: pointsEarned >= pointsTotal,
		isSkipped: false,
	};
};

export const buildSessionReportQuestionTypeRowsWithItems = ({
	tags,
	sessionItems,
	skillFilter,
}: {
	tags: MixedItemTag[];
	sessionItems: BaseItemSchema[];
	skillFilter: SkillFilter;
}): { rows: QuestionTypeRowData[]; questionItems: QuestionItem[] } => {
	const questionItems = getQuestionItemsForSkillFilter(tags, skillFilter);
	return {
		questionItems,
		rows: buildQuestionTypeRowsFromItems(questionItems, sessionItems),
	};
};
